import { IComponentConfig } from "@/app/@types/base-component";

import { getFromDictByCode, useDictStore } from "@/stores/dictStore";
import { OverheadConfig } from "../entities/overhead";
import {
    OverheadCuttingMaterialCode,
    OverheadCuttingMaterialTypeCode,
    OverheadDrawingCode,
    OverheadDrawingTypeCode,
    OverheadGasParkingTollCode,
    OverheadGasParkingTollTypeCode,
    OverheadMeasurementsCode,
    OverheadMeasurementsTypeCode,
    OverheadPackingCode,
    OverheadPackingTypeCode,
    OverheadUploadingCode,
    OverheadUploadingTypeCode,
} from "@/app/constants";

export type ConfigBuilder = {
    typeCode(value: string): ConfigBuilder;
    structureType(value: string): ConfigBuilder;
    qty(value: number): ConfigBuilder;
    withTitle(): ConfigBuilder;
    withUnit(): ConfigBuilder;
    build(): IComponentConfig;
};

const createDrawingOverheadConfig = (areaFt: number): OverheadConfig => {
    return createOverheadConfig()
        .structureType(OverheadDrawingCode)
        .typeCode(OverheadDrawingTypeCode)
        .withTitle()
        .withUnit()
        .qty(areaFt)
        .build() as OverheadConfig;
};

const createUploadingOverheadConfig = (areaFt: number): OverheadConfig => {
    return createOverheadConfig()
        .structureType(OverheadUploadingCode)
        .typeCode(OverheadUploadingTypeCode)
        .withTitle()
        .withUnit()
        .qty(areaFt)
        .build() as OverheadConfig;
};

const createCuttingMaterialOverheadConfig = (
    areaFt: number,
): OverheadConfig => {
    return createOverheadConfig()
        .structureType(OverheadCuttingMaterialCode)
        .typeCode(OverheadCuttingMaterialTypeCode)
        .withTitle()
        .withUnit()
        .qty(areaFt)
        .build() as OverheadConfig;
};

const createPackingOverheadConfig = (areaFt: number): OverheadConfig => {
    return createOverheadConfig()
        .structureType(OverheadPackingCode)
        .typeCode(OverheadPackingTypeCode)
        .withTitle()
        .withUnit()
        .qty(areaFt)
        .build() as OverheadConfig;
};

const createGasParkingTollWithDeliveryOverheadConfig = (
    areaFt: number,
): OverheadConfig => {
    return createOverheadConfig()
        .structureType(OverheadGasParkingTollCode)
        .typeCode(OverheadGasParkingTollTypeCode)
        .withTitle()
        .withUnit()
        .qty(areaFt)
        .build() as OverheadConfig;
};

const createGasParkingTollWithoutDeliveryOverheadConfig = (
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    areaFt: number,
): OverheadConfig => {
    return createOverheadConfig()
        .structureType(OverheadGasParkingTollCode)
        .typeCode(OverheadGasParkingTollTypeCode)
        .withTitle()
        .withUnit()
        .qty(areaFt)
        .build() as OverheadConfig;
};

const createMeasurementsWithInstallationOverheadConfig = (
    areaFt: number,
): OverheadConfig => {
    return createOverheadConfig()
        .structureType(OverheadMeasurementsCode)
        .typeCode(OverheadMeasurementsTypeCode)
        .withTitle()
        .withUnit()
        .qty(areaFt)
        .build() as OverheadConfig;
};

const createMeasurementsWithoutInstallationOverheadConfig =
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (areaFt: number): OverheadConfig => {
        return createOverheadConfig()
            .structureType(OverheadMeasurementsCode)
            .typeCode(OverheadMeasurementsTypeCode)
            .withTitle()
            .withUnit()
            .qty(0)
            .build() as OverheadConfig;
    };

export const OverheadItemsDefaultConfigs = (areaFt: number) => [
    createDrawingOverheadConfig(areaFt),
    createCuttingMaterialOverheadConfig(0),
    createPackingOverheadConfig(areaFt),
    createGasParkingTollWithDeliveryOverheadConfig(areaFt),
    createMeasurementsWithInstallationOverheadConfig(areaFt),
];

export const OverheadItemsWithoutDeliveryConfigs = (areaFt: number) => [
    createDrawingOverheadConfig(areaFt),
    createCuttingMaterialOverheadConfig(0),
    createPackingOverheadConfig(0),
    createGasParkingTollWithoutDeliveryOverheadConfig(areaFt),
    createMeasurementsWithInstallationOverheadConfig(areaFt),
    createUploadingOverheadConfig(0)
];

export const OverheadItemsWithoutInstallationConfigs = (areaFt: number) => [
    createDrawingOverheadConfig(areaFt),
    createCuttingMaterialOverheadConfig(areaFt),
    createPackingOverheadConfig(areaFt),
    createGasParkingTollWithDeliveryOverheadConfig(0),
    createMeasurementsWithoutInstallationOverheadConfig(0),
    createUploadingOverheadConfig(1)
];

// KOMPAS SYSTEMS
export const KompasOverheadItemsWithoutDeliveryConfigs = (areaFt: number) => [
    createDrawingOverheadConfig(areaFt),
    createCuttingMaterialOverheadConfig(areaFt),
    createPackingOverheadConfig(0),
    createGasParkingTollWithoutDeliveryOverheadConfig(areaFt),
    createMeasurementsWithInstallationOverheadConfig(areaFt),
    createUploadingOverheadConfig(0)
];

export const KompasOverheadItemsWithoutInstallationConfigs = (areaFt: number) => [
    createDrawingOverheadConfig(areaFt),
    createCuttingMaterialOverheadConfig(areaFt),
    createPackingOverheadConfig(areaFt),
    createGasParkingTollWithDeliveryOverheadConfig(0),
    createMeasurementsWithoutInstallationOverheadConfig(0),
    createUploadingOverheadConfig(1)
];

export const OverheadItemsWithoutDeliveryAndInstallationConfigs = (
    areaFt: number,
) => [
        createDrawingOverheadConfig(areaFt),
        createCuttingMaterialOverheadConfig(areaFt),
        createPackingOverheadConfig(areaFt),
        createGasParkingTollWithoutDeliveryOverheadConfig(areaFt),
        createMeasurementsWithoutInstallationOverheadConfig(areaFt),
    ];

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const OverheadItemsWithoutQtyConfigs = (areaFt: number) => [
    createDrawingOverheadConfig(0),
    createCuttingMaterialOverheadConfig(0),
    createPackingOverheadConfig(0),
    createGasParkingTollWithDeliveryOverheadConfig(0),
    createMeasurementsWithInstallationOverheadConfig(0),
];

const createOverheadConfig = (): ConfigBuilder => {
    const dictStore = useDictStore();
    const additionalCostTypes = dictStore.getAdditionalCosts("overhead");

    const config = {
        title: "",
        typeCode: "",
        structureType: "",
        unit: "",
        qty: 1,
    };

    const builder = {
        typeCode(value: string) {
            config.typeCode = value;
            return builder;
        },
        structureType(value: string) {
            config.structureType = value;
            return builder;
        },
        withTitle() {
            const title = getFromDictByCode(
                additionalCostTypes,
                config.typeCode,
            )?.title;

            config.title = title || "";
            return builder;
        },
        withUnit() {
            const unit = getFromDictByCode(additionalCostTypes, config.typeCode)
                ?.unit;

            config.unit = unit || "";
            return builder;
        },
        qty(value: number) {
            config.qty = value;
            return builder;
        },
        build() {
            return config;
        },
    };

    return builder;
};
